import React, { useState } from "react";
import styled from "styled-components";
import { PlusCircle } from "react-feather";
import { Select, SelectOption } from "@cinesys/ui/components";

/**
 * "Add a field/template/..." control shared across Spotter's field-list screens: a `Select`
 * (clicking it opens the picker menu directly) styled to look like a green success `Button` — see
 * `AddOptionSelectWrap`'s doc comment for why it's a re-skinned `Select` rather than a real
 * `Button`. Picking an option resolves it immediately, no separate pending-row step.
 */

const AddOptionOuterWrap = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

/** Border/placeholder-text color reassigned to the green tones via the same "locally re-alias the
 *  shared component's own CSS vars" technique the page's outer `Wrap` already uses, rather than
 *  fighting `Select`'s `containerStyles` (only patches the control box, not text/placeholder/
 *  focus-ring colors) or its unused-elsewhere `buttonControlProps` mode. `--inputBackground` is
 *  `transparent`, not a filled green, to match a real success `Button`'s own rest-state background
 *  (`@cinesys/ui`'s `Button` only fills on hover/focus too). */
const AddOptionSelectWrap = styled.div<{ $width: number | string }>`
  position: relative;
  width: ${(p) => (typeof p.$width === "number" ? `${p.$width}px` : p.$width)};
  --inputBackground: transparent;
  --inputBorder: var(--spotterOkLine);
  --dark400: var(--spotterOkSoft);
  --dark700: var(--spotterOk);
  --blue060: var(--spotterOk);
  --blueFocusRing: var(--spotterOkSoft);
`;

/** `z-index: 1` keeps this above the Select's own control box regardless of DOM order —
 *  react-select's control is itself `position: relative`, so once it picks up an opaque
 *  background (focus/open) it would otherwise paint over this icon despite coming first in the
 *  markup (both are positioned elements with an auto z-index, so paint order falls back to DOM
 *  order, and the control renders after the icon). */
const AddOptionIcon = styled(PlusCircle)`
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  color: var(--spotterOk);
  pointer-events: none;
`;

export interface AddOptionSelectProps {
  options: SelectOption[];
  placeholder: string;
  onSelect: (value: string) => void;
  portalTarget: HTMLDivElement | null;
  width?: number | string;
}

const AddOptionSelect: React.FC<AddOptionSelectProps> = ({
  options,
  placeholder,
  onSelect,
  portalTarget,
  width = 320,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <AddOptionOuterWrap>
      <AddOptionSelectWrap $width={width}>
        {!menuOpen && <AddOptionIcon size={16} />}
        <Select
          placeholder={placeholder}
          isSearchable
          options={options}
          value={null}
          onChange={(option) => {
            const value = (option as SelectOption)?.value as string | undefined;
            if (value) {
              onSelect(value);
            }
          }}
          onMenuOpenChange={setMenuOpen}
          portalTarget={portalTarget}
          noPadding
          containerStyles={
            menuOpen
              ? { fontWeight: 600 }
              : { paddingLeft: 20, fontWeight: 600 }
          }
        />
      </AddOptionSelectWrap>
    </AddOptionOuterWrap>
  );
};

export default AddOptionSelect;
